import { Injectable } from '@angular/core';
import { BUTTON_TYPES, ITutorial, NavigationLinkType } from '@sv/interfaces';
import Shepherd from 'shepherd.js';

@Injectable({
  providedIn: 'root'
})
export class TutorialService {
  tutorial: ITutorial;

  constructor() {}

  setTutorial(tutorial) {
    this.tutorial = tutorial;
  }

  /* set the options that will be applied to each step by default */
  getDefaultStepOptions() {
    return {
      scrollTo: true,
      cancelIcon: {
        enabled: true
      },
      modalOverlayOpeningRadius: 10,
      when: {
        cancel() {
          sessionStorage.setItem('tutorial', 'false');
        }
      }
    };
  }

  getReceptionSteps(showDownloads: boolean, showSupport: boolean) {
    const receptionSteps = this.tutorial.reception;
    const returnSteps = [];
    let buttonDone = '';
    let buttonNext = '';
    let buttonBack = '';

    for (const button of receptionSteps.welcome.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.NEXT) {
        buttonNext = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    returnSteps.push({
      id: 'welcome',
      buttons: [
        {
          classes: 'tutorial',
          text: buttonDone,
          action() {
            sessionStorage.setItem('tutorial', 'false');
            this.cancel();
          }
        },
        {
          classes: 'next-button',
          text: buttonNext,
          type: 'next'
        }
      ],
      title: receptionSteps.welcome.title,
      text: [receptionSteps.welcome.text]
    });

    for (const button of receptionSteps.start.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.NEXT) {
        buttonNext = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    returnSteps.push({
      id: 'reception',
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonBack,
          type: 'back'
        },
        {
          classes: 'shepherd-button-primary',
          text: buttonNext,
          type: 'next'
        }
      ],
      title: receptionSteps.start.title,
      text: [receptionSteps.start.text]
    });

    for (const button of receptionSteps.agenda.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.NEXT) {
        buttonNext = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    returnSteps.push({
      id: 'reception-agenda',
      attachTo: {
        element: '.agenda-wrapper',
        on: 'left'
      },
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonBack,
          type: 'back'
        },
        {
          classes: 'shepherd-button-primary',
          text: buttonNext,
          type: 'next'
        }
      ],
      highlightClass: 'highlight',
      title: receptionSteps.agenda.title,
      text: [receptionSteps.agenda.text]
    });

    for (const button of receptionSteps.navAgenda.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.NEXT) {
        buttonNext = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    returnSteps.push({
      id: 'nav-agenda',
      attachTo: {
        element: '#navAgenda',
        on: 'top'
      },
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonBack,
          type: 'back'
        },
        {
          classes: 'shepherd-button-primary',
          text: buttonNext,
          type: 'next'
        }
      ],
      highlightClass: 'nav-highlight',
      showOn: () => {
        // not on mobile
        return screen.width > 699;
      },
      title: receptionSteps.navAgenda.title,
      text: [receptionSteps.navAgenda.text]
    });

    for (const button of receptionSteps.navDownloads.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.NEXT) {
        buttonNext = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    returnSteps.push({
      id: 'nav-downloads',
      attachTo: {
        element: '#navDownloads',
        on: 'top'
      },
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonBack,
          type: 'back'
        },
        {
          classes: 'shepherd-button-primary',
          text: buttonNext,
          type: 'next'
        }
      ],
      highlightClass: 'nav-highlight',
      showOn: () => {
        // not on mobile
        return showDownloads && screen.width > 699;
      },
      title: receptionSteps.navDownloads.title,
      text: [receptionSteps.navDownloads.text]
    });

    for (const button of receptionSteps.navSupport.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.NEXT) {
        buttonNext = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    returnSteps.push({
      id: 'nav-support',
      attachTo: {
        element: '#navSupport',
        on: 'top'
      },
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonBack,
          type: 'back'
        },
        {
          classes: 'shepherd-button-primary',
          text: buttonNext,
          type: 'next'
        }
      ],
      highlightClass: 'nav-highlight',
      showOn: () => {
        // not on mobile
        return showSupport && screen.width > 699;
      },
      title: receptionSteps.navSupport.title,
      text: [receptionSteps.navSupport.text]
    });

    return returnSteps;
  }

  getKeynoteSteps() {
    const keynoteSteps = this.tutorial.keynote;
    const returnSteps = [];
    let buttonDone = '';
    let buttonNext = '';
    let buttonBack = '';

    for (const button of keynoteSteps.start.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.NEXT) {
        buttonNext = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    returnSteps.push({
      id: 'keynote',
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonNext,
          type: 'next'
        }
      ],
      title: keynoteSteps.start.title,
      text: [keynoteSteps.start.text]
    });

    for (const button of keynoteSteps.stream.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.NEXT) {
        buttonNext = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    returnSteps.push({
      id: 'keynote-stream',
      attachTo: {
        element: '#stream',
        on: 'right'
      },
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonBack,
          type: 'back'
        },
        {
          classes: 'shepherd-button-primary',
          text: buttonNext,
          type: 'next'
        }
      ],
      highlightClass: 'highlight',
      title: keynoteSteps.stream.title,
      text: [keynoteSteps.stream.text]
    });

    for (const button of keynoteSteps.slido.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.NEXT) {
        buttonNext = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    returnSteps.push({
      id: 'keynote-slido',
      attachTo: {
        element: '#slido',
        on: 'left'
      },
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonBack,
          type: 'back'
        },
        {
          classes: 'shepherd-button-primary',
          text: buttonNext,
          type: 'next'
        }
      ],
      highlightClass: 'highlight',
      title: keynoteSteps.slido.title,
      text: [keynoteSteps.slido.text]
    });
    return returnSteps;
  }

  getWorkshopSteps() {
    const workshopSteps = this.tutorial.workshop;
    const returnSteps = [];
    let buttonDone = '';
    let buttonNext = '';
    let buttonBack = '';

    for (const button of workshopSteps.start.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.NEXT) {
        buttonNext = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    returnSteps.push({
      id: 'workshop',
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonNext,
          type: 'next'
        }
      ],
      title: workshopSteps.start.title,
      text: [workshopSteps.start.text]
    });

    for (const button of workshopSteps.workshopEnter.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.NEXT) {
        buttonNext = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    returnSteps.push({
      id: 'workshop-enter',
      attachTo: {
        element: '#workshopEnter',
        on: 'right'
      },
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonBack,
          type: 'back'
        },
        {
          classes: 'shepherd-button-primary',
          text: buttonNext,
          type: 'next'
        }
      ],
      canClickTarget: false,
      highlightClass: 'highlight',
      title: workshopSteps.workshopEnter.title,
      text: [workshopSteps.workshopEnter.text]
    });

    return returnSteps;
    /* {
        id: 'workshop-back',
        attachTo: {
          element: '#workshopBack',
          on: 'bottom'
        },
        buttons: [
          {
            classes: 'shepherd-button-primary',
            text: 'Next',
            type: 'next'
          }
        ],
        highlightClass: 'highlight',
        title: 'Workshop verlassen',
        text: ['Wenn Sie hier klicken, verlassen Sie den Workshop und gelangen zur Übersicht zurück.'],
        when: {
          show: function() {
            waits(500);
          }
        }
      }, */
  }

  getNetworkingSteps() {
    const networkingSteps = this.tutorial.networking;
    const returnSteps = [];
    let buttonDone = '';
    let buttonNext = '';
    let buttonBack = '';

    for (const button of networkingSteps.start.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.NEXT) {
        buttonNext = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    returnSteps.push({
      id: 'networking',
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonNext,
          type: 'next'
        }
      ],
      title: networkingSteps.start.title,
      text: [networkingSteps.start.text]
    });

    for (const button of networkingSteps.networkingFunction.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.NEXT) {
        buttonNext = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    returnSteps.push({
      id: 'networking-function',
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonBack,
          type: 'back'
        },
        {
          classes: 'shepherd-button-primary',
          text: buttonNext,
          type: 'next'
        }
      ],
      title: networkingSteps.networkingFunction.title,
      text: [networkingSteps.networkingFunction.text]
    });

    return returnSteps;
  }

  getFairSteps() {
    const fairSteps = this.tutorial.tradefair;
    const returnSteps = [];
    let buttonDone = '';
    let buttonNext = '';
    let buttonBack = '';

    for (const button of fairSteps.start.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.NEXT) {
        buttonNext = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    returnSteps.push({
      id: 'fair',
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonNext,
          type: 'next'
        }
      ],
      title: fairSteps.start.title,
      text: [fairSteps.start.text]
    });

    return returnSteps;
  }

  getNextModuleStep(links, callingModule: NavigationLinkType) {
    const types = [];
    for (const link of links) {
      types.push(link.type);
    }

    if (types.indexOf(NavigationLinkType.Keynote) !== -1 && callingModule === NavigationLinkType.Reception) {
      return this.getNextStepKeynote();
    } else if (
      types.indexOf(NavigationLinkType.Workshop) !== -1 &&
      (callingModule === NavigationLinkType.Reception || callingModule === NavigationLinkType.Keynote)
    ) {
      return this.getNextStepWorkshop();
    } else if (
      types.indexOf(NavigationLinkType.Networking) !== -1 &&
      callingModule !== NavigationLinkType.Networking &&
      callingModule !== NavigationLinkType.Fair
    ) {
      return this.getNextStepNetworking();
    } else if (types.indexOf(NavigationLinkType.Fair) !== -1 && callingModule !== NavigationLinkType.Fair) {
      return this.getNextStepFair();
    } else {
      return this.getLastStep();
    }
  }

  /* Modul Steps */

  getNextStepKeynote(): Shepherd.Step.StepOptions {
    const navKeynote = this.tutorial.keynote.navKeynote;
    let buttonDone = '';
    let buttonBack = '';

    for (const button of navKeynote.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    return {
      id: 'nav-modul',
      attachTo: {
        element: '#navKeynote',
        on: 'bottom'
      },
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonBack,
          action() {
            this.back();
          }
        },
        {
          classes: 'shepherd-button-primary',
          text: buttonDone,
          action() {
            sessionStorage.setItem('tutorial', 'false');
            this.cancel();
          }
        }
      ],
      highlightClass: 'nav-highlight',
      title: navKeynote.title,
      text: [navKeynote.text]
    };
  }

  getNextStepWorkshop(): Shepherd.Step.StepOptions {
    const navWorkshop = this.tutorial.workshop.navWorkshop;
    let buttonDone = '';
    let buttonBack = '';

    for (const button of navWorkshop.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    return {
      id: 'nav-modul',
      attachTo: {
        element: '#navWorkshop',
        on: 'bottom'
      },
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonBack,
          action() {
            this.back();
          }
        },
        {
          classes: 'shepherd-button-primary',
          text: buttonDone,
          action() {
            sessionStorage.setItem('tutorial', 'false');
            this.cancel();
          }
        }
      ],
      highlightClass: 'nav-highlight',
      title: navWorkshop.title,
      text: [navWorkshop.text]
    };
  }

  getNextStepNetworking(): Shepherd.Step.StepOptions {
    const navNetworking = this.tutorial.networking.navNetworking;
    let buttonDone = '';
    let buttonBack = '';

    for (const button of navNetworking.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    return {
      id: 'nav-modul',
      attachTo: {
        element: '#navNetworking',
        on: 'bottom'
      },
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonBack,
          action() {
            this.back();
          }
        },
        {
          classes: 'shepherd-button-primary',
          text: buttonDone,
          action() {
            sessionStorage.setItem('tutorial', 'false');
            this.cancel();
          }
        }
      ],
      highlightClass: 'nav-highlight',
      title: navNetworking.title,
      text: [navNetworking.text]
    };
  }

  getNextStepFair(): Shepherd.Step.StepOptions {
    const navFair = this.tutorial.tradefair.navTradeFair;
    let buttonDone = '';
    let buttonBack = '';

    for (const button of navFair.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    return {
      id: 'nav-modul',
      attachTo: {
        element: '#navFair',
        on: 'bottom'
      },
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonBack,
          action() {
            this.back();
          }
        },
        {
          classes: 'shepherd-button-primary',
          text: buttonDone,
          action() {
            sessionStorage.setItem('tutorial', 'false');
            this.cancel();
          }
        }
      ],
      highlightClass: 'nav-highlight',
      title: navFair.title,
      text: [navFair.text]
    };
  }

  getLastStep(): Shepherd.Step.StepOptions {
    const finishStep = this.tutorial.finish;
    let buttonDone = '';
    let buttonBack = '';

    for (const button of finishStep.buttons) {
      if (button.type === BUTTON_TYPES.DONE) {
        buttonDone = button.text;
      }
      if (button.type === BUTTON_TYPES.BACK) {
        buttonBack = button.text;
      }
    }
    return {
      id: 'done',
      buttons: [
        {
          classes: 'shepherd-button-primary',
          text: buttonBack,
          action() {
            this.back();
          }
        },
        {
          classes: 'shepherd-button-primary',
          text: buttonDone,
          action() {
            sessionStorage.setItem('tutorial', 'false');
            this.cancel();
          }
        }
      ],
      highlightClass: 'nav-highlight',
      title: finishStep.title,
      text: [finishStep.text]
    };
  }
}
